import { useState, useEffect, useCallback, useRef } from 'react';
import { isTouch, readBatchFlags, writeBatchFlags } from './util';

export const useImageZipper = () => {
    const [worker, setWorker] = useState(null);
    const [isZipping, setIsZipping] = useState(false);
    const [progress, setProgress] = useState(0);
    const [downloadKey, setDownloadKey] = useState()
    const [error, setError] = useState(null);
    const [zipFilename, setZipFilename] = useState('images.zip');
    // The URL lists are re-fetched from the API on every page load, so they are held here
    // instead of localStorage — persisting them overflows the quota on large works.
    const batchesRef = useRef([]);

    useEffect(() => {
        if(worker && downloadKey){
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.register('/service-worker.js')
                .catch(error => {
                    console.error('Service Worker registration failed:', error);
                });
            }
            processNextBatch()
        }
    }, [worker, downloadKey]);

        const createWorker = async () => {
            const response = await fetch('/service-worker.js');
            const workerCode = await response.text();
            const blob = new Blob([workerCode], { type: 'application/javascript' });
            const workerUrl = URL.createObjectURL(blob);
            const webWorker = new Worker(workerUrl);
            setWorker(webWorker);

            return () => {
                webWorker.terminate();
                URL.revokeObjectURL(workerUrl);
            };
        };

        

    const zipImages = useCallback((batchUrls) => {
        if (!worker || !batchUrls.length) return;
        
        setIsZipping(true);
        setProgress(0);
        setError(null);

        return new Promise((resolve, reject) => {
            worker.onmessage = (event) => {
                const { type, url, message, progress } = event.data;
                switch (type) {
                    case 'PROGRESS':
                        setProgress(progress);
                        break;
                    case 'COMPLETE':
                        setIsZipping(false);
                        setProgress(100);
                        resolve(url);
                        break;
                    case 'ERROR':
                        setIsZipping(false);
                        setError(message);
                        reject(new Error(message));
                        break;
                }
            };

            worker.postMessage({
                type: 'START_ZIP',
                urls: batchUrls
            });
        });
    }, [worker]);

    const buildBatches = (urlData) => urlData.map(item => ({
        batch_name: item.batch_name,
        urls: item.url_data.map(data => data.download_url)
    }));

    // Only the download flags are stored — just enough to resume after the reload
    // between batches.
    const initializeBatches = (batches, dataKey) => {
        const storedFlags = readBatchFlags(dataKey);
        const nextBatchIndex = getNextBatchIndex(dataKey);
        const canResume = storedFlags.length === batches.length && nextBatchIndex >= 0;
        if (!canResume) {
            writeBatchFlags(dataKey, batches.map(batch => ({
                batch_name: batch.batch_name,
                is_downloaded: false
            })));
        }
    };

    const getNextBatchIndex = (dataKey) => {
        return readBatchFlags(dataKey).findIndex(batch => !batch.is_downloaded);
    };

    const updateBatchStatus = (batchIndex) => {
        const storedFlags = readBatchFlags(downloadKey);
        if (storedFlags[batchIndex]) {
            storedFlags[batchIndex].is_downloaded = true;
            writeBatchFlags(downloadKey, storedFlags);
        }
    };

    const downloadZip = async (urlData, filename = 'images.zip', dataKey) => {
        const batches = buildBatches(urlData);
        batchesRef.current = batches;
        setDownloadKey(dataKey)
        setZipFilename(filename);
        initializeBatches(batches, dataKey);
        if (!worker) {
            createWorker();
        }
    };

    const processNextBatch = async () => {
        const nextBatchIndex = getNextBatchIndex(downloadKey);
        const batches = batchesRef.current;

        if (nextBatchIndex >= 0 && batches[nextBatchIndex]) {
            localStorage.setItem('downloadBatch', true);

            try {
                const downloadUrl = await zipImages(batches[nextBatchIndex].urls);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `${batches[nextBatchIndex].batch_name}.zip`;
                window.__LAST_DOWNLOAD_NAME__ = link.download;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);

                updateBatchStatus(nextBatchIndex); // Mark batch as downloaded

                if(!batches[nextBatchIndex + 1]){
                    localStorage.removeItem('downloadBatch');
                    // localStorage.removeItem(downloadKey);
                }
                var delay = isTouch() ? 12000 : 8000
                // Reload to trigger the next batch
                setTimeout(() => {
                    window.location.reload();
                }, delay);
            } catch (err) {
                setError(err.message);
                throw err;
            }
        } else {
            localStorage.removeItem('downloadBatch');
            // localStorage.removeItem(downloadKey);
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    };

    return {
        downloadZip,
        getNextBatchIndex,
        isZipping,
        progress,
        error
    };
};
